import React from 'react';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import {Button, Detail, DropdownList, EMPHASIS, FlexColumn, FlexRow, Icon} from '@forecast-it/design-system';
import {LabelledIconButton} from '../labelled-icon-button/LabelledIconButton';
import {
	DAY_OF_MONTH_MESSAGE,
	daysOfMonthFor,
	DEFAULT_DAY_OF_MONTH,
	DEFAULT_WEEKDAYS,
	FREQUENCIES,
	FREQUENCY_MESSAGE,
	TIME_SLOTS,
	formatTimeOfDay,
	isWeeklyFrequency,
	usesTwelveHourClock,
} from './scheduleOptions';
import {displayedTime} from './scheduleTimezone';
import type {ScheduleChange, ScheduleSnapshot} from './scheduleMutations';
import {SCHEDULING_MESSAGE} from './schedulingMessages';
import {WeekdayButtons} from './WeekdayButtons';

const MENU_OFFSET_PIXELS = 4;

const Block = styled.div`
	border: 1px solid ${({theme}) => theme.sys.color.border.decorative};
	border-radius: ${({theme}) => theme.sys.size.borderradius['3']};
	padding: 12px;
`;

// The change to send for an edit. Switching family brings the fields the service needs with it: a day
// rule for monthly and quarterly, and Monday for a weekly schedule with no day. A picked time is a time in
// the viewer's zone, so it is sent with that zone. Any other edit sends neither, and the schedule keeps its
// stored time and zone: rewriting them into the viewer's zone at today's offset would move the run by an
// hour whenever the two zones change clocks on different dates.
export const timingChange = (schedule: ScheduleSnapshot, change: ScheduleChange, viewerZone: string): ScheduleChange => {
	const result: ScheduleChange = {...change};
	if (change.frequency && change.frequency !== schedule.frequency) {
		const toWeekly = isWeeklyFrequency(change.frequency);
		if (!toWeekly && isWeeklyFrequency(schedule.frequency)) {
			result.dayOfMonth = DEFAULT_DAY_OF_MONTH;
		}
		// A day rule the new frequency does not offer (the 15th, for a quarterly schedule) falls back to the default.
		if (!toWeekly && schedule.dayOfMonth !== null && !daysOfMonthFor(change.frequency).includes(schedule.dayOfMonth)) {
			result.dayOfMonth = DEFAULT_DAY_OF_MONTH;
		}
		if (toWeekly && !isWeeklyFrequency(schedule.frequency)) {
			result.dayOfMonth = null;
			if (schedule.weekdays.length === 0) {
				result.weekdays = DEFAULT_WEEKDAYS;
			}
		}
	}
	if (change.timeOfDay !== undefined) {
		return {...result, timezone: viewerZone};
	}
	return result;
};

type PickerProps<T extends string> = {
	value: T;
	label: string;
	options: ReadonlyArray<{value: T; label: string}>;
	disabled: boolean;
	onPick: (value: T) => void;
	cy: string;
};

const Picker = <T extends string>({value, label, options, disabled, onPick, cy}: PickerProps<T>): JSX.Element => {
	const [isOpen, setOpen] = React.useState(false);
	return (
		<DropdownList open={isOpen} onOpenChange={setOpen}>
			<DropdownList.Trigger disabled={disabled}>
				<Button emphasis={EMPHASIS.MEDIUM} rightIcon={'chevronDown'} disabled={disabled} data-cy={cy}>
					{label}
				</Button>
			</DropdownList.Trigger>
			<DropdownList.Content align={'start'} side={'bottom'} offset={MENU_OFFSET_PIXELS} portalled={true}>
				{options.map(option => (
					<DropdownList.Item
						key={option.value}
						onClick={() => {
							setOpen(false);
							if (option.value !== value) {
								onPick(option.value);
							}
						}}
					>
						{option.label}
					</DropdownList.Item>
				))}
			</DropdownList.Content>
		</DropdownList>
	);
};

type ScheduleRowProps = {
	schedule: ScheduleSnapshot;
	disabled: boolean;
	viewerZone: string;
	onChange: (change: ScheduleChange) => void;
	onRemove: () => void;
};

export const ScheduleRow = ({schedule, disabled, viewerZone, onChange, onRemove}: ScheduleRowProps): JSX.Element => {
	const {formatMessage, locale} = useIntl();
	const twelveHour = usesTwelveHourClock(locale);
	const shown = displayedTime(schedule.timeOfDay, schedule.timezone, viewerZone);
	const weekly = isWeeklyFrequency(schedule.frequency);
	const send = (change: ScheduleChange) => onChange(timingChange(schedule, change, viewerZone));
	const timeOptions = (TIME_SLOTS.includes(shown.timeOfDay) ? TIME_SLOTS : [...TIME_SLOTS, shown.timeOfDay].sort()).map(
		slot => ({
			value: slot,
			label: formatTimeOfDay(slot, twelveHour),
		})
	);

	return (
		<Block data-cy={'schedule-row'}>
			<FlexRow alignItems={'flex-start'} gap={'s'}>
				<Icon icon={'clock'} size={'m'} />
				<FlexColumn gap={'s'} flex={'1 1 auto'}>
					<FlexRow gap={'s'} wrap alignItems={'center'} data-cy={'schedule-controls'}>
						<Picker
							value={schedule.frequency}
							label={formatMessage({id: FREQUENCY_MESSAGE(schedule.frequency)})}
							options={FREQUENCIES.map(value => ({value, label: formatMessage({id: FREQUENCY_MESSAGE(value)})}))}
							disabled={disabled}
							onPick={frequency => send({frequency})}
							cy={'schedule-frequency'}
						/>
						{!weekly && schedule.dayOfMonth ? (
							<Picker
								value={schedule.dayOfMonth}
								label={formatMessage({id: DAY_OF_MONTH_MESSAGE(schedule.dayOfMonth)})}
								options={daysOfMonthFor(schedule.frequency).map(value => ({
									value,
									label: formatMessage({id: DAY_OF_MONTH_MESSAGE(value)}),
								}))}
								disabled={disabled}
								onPick={dayOfMonth => send({dayOfMonth})}
								cy={'schedule-day-of-month'}
							/>
						) : null}
						<Picker
							value={shown.timeOfDay}
							label={formatTimeOfDay(shown.timeOfDay, twelveHour)}
							options={timeOptions}
							disabled={disabled}
							onPick={timeOfDay => send({timeOfDay})}
							cy={'schedule-time'}
						/>
					</FlexRow>
					{weekly ? (
						<WeekdayButtons
							selected={schedule.weekdays}
							disabled={disabled}
							onChange={weekdays => send({weekdays})}
						/>
					) : null}
					{shown.kind === 'foreign' ? (
						<Detail color={'auxiliary'}>
							{formatMessage({id: SCHEDULING_MESSAGE.RUNS_IN}, {timezone: shown.timezone})}
						</Detail>
					) : null}
				</FlexColumn>
				<LabelledIconButton
					icon={'close'}
					label={formatMessage({id: SCHEDULING_MESSAGE.REMOVE})}
					disabled={disabled}
					onPress={onRemove}
					data-cy={'schedule-remove'}
				/>
			</FlexRow>
		</Block>
	);
};
